// Copyright (c) 2022 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";

import { IAction } from "actions";
import { round } from "shared/utils/utils";
import { IState, IStateIdList } from "states/states";
import { IConnectorData, IOcppCharger } from "states/ocppstates";
import Connector, { ConnectorStatus, getColor } from "model/connector";

import IconChargerSessionList from "icons/navChargerSessionList";

interface IChargerStatisticsProps {
  chargers: IOcppCharger[];
  connectorData: (IConnectorData | undefined)[][];
  lastOcppMessage: IStateIdList<number | undefined>;
}

const mapStateToProps = (state: IState) => {
  const props: IChargerStatisticsProps = {
    chargers: state.ocpp.charger,
    connectorData: state.ocpp.connectorData,
    lastOcppMessage: state.ocpp.lastOcppMessage,
  };
  return props;
};

interface IChargerStatisticsDispatch {}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IChargerStatisticsDispatch = {};
  return dispatchProps;
};

const messages = defineMessages({});

class ChargerStatisticsComponent extends React.Component<
  IChargerStatisticsProps & IChargerStatisticsDispatch
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    // these items are potential row entries
    const statEntries = [
      ConnectorStatus.AVAILABLE,
      ConnectorStatus.UNAVAILABLE,
      ConnectorStatus.CHARGING,
      ConnectorStatus.SUSPENDED_EV,
      undefined, // offline, unknown
    ];
    const statCount: number[] = Array(statEntries.length).fill(0);
    let power = 0;

    // loop through all connectors
    this.props.chargers.forEach((curCharger, chargerIndex) => {
      const nrConnectors = Math.max(
        curCharger.numberofconnectors ?? (this.props.connectorData[chargerIndex]?.length ?? 2) - 1,
        1,
      );
      Array.from(Array(nrConnectors + 1).keys()).forEach((connectorIndex) => {
        // we skip direct charger entries
        if (connectorIndex !== 0) {
          const connector = new Connector(
            this.props.connectorData[chargerIndex],
            connectorIndex,
            this.props.lastOcppMessage[chargerIndex],
          );
          if (connector.isOnline() && connector.status === ConnectorStatus.CHARGING) {
            power += connector.data?.power ?? 0;
          }
          // search that status that has the same color
          statEntries.forEach((curStat, curStatIndex) => {
            if (connector.getStatusColor() === getColor(curStat, true)) {
              statCount[curStatIndex] += 1;
            }
          });
        }
      });
    });

    const rows = statEntries.map((curStat, curIndex) => {
      return (
        <tr key={curStat ?? "undefined"}>
          <td>{this.renderStatusIcon(curStat)}</td>
          <td className="right" style={{ paddingRight: 0 }}>
            {statCount[curIndex]}
          </td>
          <td>
            {curStat !== ConnectorStatus.SUSPENDED_EV ?
              <FormattedMessage id={`ChargerStatus_${curStat ?? ""}`} />
            : <FormattedMessage id="Suspended" />}
          </td>
        </tr>
      );
    });

    return (
      <>
        <div id="chargerstatistics">
          <div id="chargerstatisticsframe">
            <table>
              <tbody>{rows}</tbody>
            </table>
            <div>
              <IconChargerSessionList />
              <div id="totalpower">{round(power, 1)} kW</div>
            </div>
          </div>
        </div>
      </>
    );
  }

  private renderStatusIcon(status: ConnectorStatus | undefined) {
    const color = getColor(status, true);
    return (
      <div
        style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: color }}
        title={this.context.formatMessage({ id: `ChargerStatus_${status ?? ""}` })}
      ></div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ChargerStatisticsComponent);
